import { rgbToHex, useTheme } from '@mui/material';
import { captureException } from '@sentry/react';
import {
  Appearance,
  Stripe,
  StripeElements,
  StripeElementsOptionsMode,
  StripeError,
  StripePaymentElementOptions,
} from '@stripe/stripe-js';
import { loadStripe } from '@stripe/stripe-js/pure';
import { usePostHog } from 'posthog-js/react';
import {
  createContext,
  PropsWithChildren,
  useContext,
  useEffect,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';

import { shopClient } from '@portal-clients/shop';

import { tApp } from '@frontend/locales';
import { PortalToStripeLocaleMapper } from '@frontend/mappers';

import { usePortalConfiguration } from './configuration-context';
import { paymentUtils } from './payment-utils';
import { routes } from './routes';
import { useShopContext } from './shop-context';
import { useUser } from './user-context';

export interface StripeContextType {
  stripePromise: Promise<Stripe | null> | undefined;
  loadStripeWithRetry: () => Promise<Stripe | null>;
  stripe: Stripe | undefined;
  elements: StripeElements | undefined;
  setStripe(stripe: Stripe | undefined): void;
  setElements(elements: StripeElements | undefined): void;
  setAmount(amount: number): void;
  elementOptions: StripeElementsOptionsMode | undefined;
  paymentElementOptions: StripePaymentElementOptions;
  elementsInputValid: boolean;
  updateElementsInputValidity(valid: boolean): void;
  submit(
    orderType: string,
    orderId: string,
    accountId: string,
    clientSecret: string,
  ): Promise<StripeError | undefined>;
}

export const StripeContext = createContext<StripeContextType>(null!);

export const StripeProvider = (props: PropsWithChildren) => {
  const [stripePromise, setStripePromise] = useState<
    Promise<Stripe | null> | undefined
  >(undefined);
  const [stripe, setStripe] = useState<Stripe | undefined>(undefined);
  const [elements, setElements] = useState<StripeElements | undefined>(
    undefined,
  );
  const [amount, setAmount] = useState<number>(1);
  const [elementOptions, setElementOptions] = useState<
    StripeElementsOptionsMode | undefined
  >(undefined);
  const [isElementsCheckoutInputsValid, setIsElementsCheckoutInputsValid] =
    useState(false);
  const navigate = useNavigate();
  const posthog = usePostHog();
  const { config } = usePortalConfiguration();
  const topUpShopId = config?.topupConfig?.shopId;
  const { shop } = useShopContext();
  const [stripeCustomerSession, setStripeCustomerSession] = useState<
    string | undefined
  >(undefined);
  const [loadingCustomerSessionSecret, setLoadingCustomerSessionSecret] =
    useState<boolean>(true);

  const theme = useTheme();
  const { user } = useUser();

  const getStripePromise = () => {
    return loadStripe(APP_CONFIG.stripe.apiKey);
  };

  // To avoid "Failed to load stripe.js" error we retry reloading stripe.
  const loadStripeWithRetry = async () => {
    const MAX_RETRIES = 3;
    const DELAY = 3000;
    let lastError: Error | undefined;

    for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
      try {
        const stripe = await getStripePromise();

        if (stripe) {
          setStripePromise(Promise.resolve(stripe));

          if (attempt > 1) {
            posthog.capture('stripe_load_retry_success_attempt', { attempt });
          }

          return stripe;
        }
      } catch (error) {
        lastError = error as Error;
      }

      if (attempt < MAX_RETRIES) {
        await new Promise((res) => setTimeout(res, DELAY));
      }
    }

    captureException(lastError, {
      level: 'error',
      extra: {
        message: `Failed to load Stripe.js after ${MAX_RETRIES} attempts`,
      },
    });
    return null;
  };

  const appearance: Appearance = {
    theme: 'stripe',
    variables: {
      colorPrimary: theme.palette.primary.main,
      colorBackground: theme.palette.background.paper,
      colorDanger: theme.palette.error.main,
      colorSuccess: theme.palette.success.main,
      colorWarning: theme.palette.warning.main,

      iconCheckmarkColor: theme.palette.primary.main,

      colorText: rgbToHex(theme.palette.text.primary),
      colorTextSecondary: rgbToHex(theme.palette.text.primary),
      colorTextPlaceholder: rgbToHex(theme.palette.text.disabled),

      fontFamily: theme.typography.body1.fontFamily,
      fontSizeBase: `${theme.typography.body1.fontSize}`,
      fontSizeXs: `${theme.typography.body1.fontSize}`,
      fontLineHeight: `${theme.typography.body1.lineHeight}`,

      borderRadius: `${theme.shape.borderRadius}px`,
      accordionItemSpacing: theme.spacing(2),
      spacingUnit: theme.spacing(0.75),
    },
    rules: {
      '.AccordionItem': {
        boxShadow: 'none',
        border: `1px solid ${theme.palette.divider}`,
      },
      '.AccordionItem:hover': {
        backgroundColor: theme.palette.action.hover,
      },
      '.AccordionItem--selected': {
        border: `2px solid ${theme.palette.primary.main}`,
      },
      '.AccordionItem--selected:hover': {
        backgroundColor: theme.palette.background.paper,
      },
      '.Input': {
        backgroundColor: theme.palette.background.paper,
        border: `1px solid ${theme.palette.divider}`,
        boxShadow: 'none',
        outline: 'none',
      },
      '.Input:focus': {
        boxShadow: 'none',
        outline: `1px solid ${theme.palette.primary.main}`,
      },
      '.Block': {
        boxShadow: 'none',
      },
    },
  };

  useEffect(() => {
    if (!topUpShopId || stripeCustomerSession) {
      setLoadingCustomerSessionSecret(false);
      return;
    }

    shopClient
      .getStripeCustomerSession(topUpShopId)
      .then((response) => {
        setStripeCustomerSession(response.clientSecret);
      })
      .catch(captureException)
      .finally(() => {
        setLoadingCustomerSessionSecret(false);
      });
  }, [topUpShopId]);

  useEffect(() => {
    if (!elements) {
      return;
    }

    elements.update({
      amount: amount,
    });
  }, [amount, elements]);

  useEffect(() => {
    if (!shop?.stripeConnectId || loadingCustomerSessionSecret) {
      return;
    }

    const elementOptions: StripeElementsOptionsMode = {
      locale: PortalToStripeLocaleMapper(user.locale),
      currency: config.currency.toLowerCase(),
      mode: 'payment',
      captureMethod: 'automatic',
      onBehalfOf: shop.stripeConnectId,
      appearance,
      customerSessionClientSecret: stripeCustomerSession,
      amount: amount,
    };

    setElementOptions(elementOptions);
  }, [shop?.stripeConnectId, config.currency, loadingCustomerSessionSecret]);

  const paymentElementOptions: StripePaymentElementOptions = {
    fields: {
      billingDetails: {
        address: 'if_required',
      },
    },
    defaultValues: {
      billingDetails: {
        name:
          user?.firstName && user?.lastName
            ? `${user.firstName} ${user.lastName}`
            : undefined,
        email: user?.email,
      },
    },
    layout: { type: 'accordion', spacedAccordionItems: true },
  };

  const updateTotalAmount = (total: number) => {
    if (total > 0 && total !== amount) {
      setAmount(total);
    }
  };

  const submitStripePayment = async (
    orderType: string,
    orderId: string,
    accountId: string,
    paymentIntentSecret: string,
  ): Promise<StripeError | undefined> => {
    if (!stripe || !elements) {
      return;
    }

    const { error: submitError } = await elements.submit();

    if (submitError) {
      posthog.capture('payment_stripe_submit_failed');
      captureException(submitError);
      return handleStripeError(submitError);
    }

    const { error: confirmError, paymentIntent: response } =
      await stripe.confirmPayment({
        elements,
        clientSecret: paymentIntentSecret,
        confirmParams: {
          return_url: paymentUtils.getReturnUrl(orderType, orderId, accountId),
        },
        redirect: 'if_required',
      });

    if (confirmError) {
      posthog.capture('payment_stripe_confirm_failed');
      captureException(confirmError);
      return handleStripeError(confirmError);
    }

    // Handle the response from Stripe unless selected payment method requires an external redirect.
    if (response.status === 'succeeded') {
      posthog.capture('payment_stripe_successful');
      navigate(routes.payment.payment(orderType, orderId, accountId));
    }

    return;
  };

  /** Determines what error messages from stripe that should be reflected to the user */
  const handleStripeError = (error: StripeError): StripeError => {
    switch (error.type) {
      case 'api_connection_error':
      case 'api_error':
      case 'authentication_error':
      case 'idempotency_error':
        return { ...error, message: tApp.errorPages.unexpectedError() };
      default:
        return error;
    }
  };

  return (
    <StripeContext.Provider
      value={{
        stripePromise,
        loadStripeWithRetry,
        stripe,
        elements,
        setStripe,
        setElements,
        setAmount: updateTotalAmount,
        elementOptions,
        paymentElementOptions,
        elementsInputValid: isElementsCheckoutInputsValid,
        updateElementsInputValidity: setIsElementsCheckoutInputsValid,
        submit: submitStripePayment,
      }}
    >
      {props.children}
    </StripeContext.Provider>
  );
};

export const useStripeContext = () => {
  return useContext(StripeContext);
};
